import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import type { WebinarStatsRow } from '../types';
import { WebinarStatusTag } from './WebinarStatusTag';
import { useNavigate } from 'react-router-dom';
import { getFormattedDate } from '@/lib/utils';
import { resolveNotStartedKpiValue, resolvePresenceRateTone } from '@/lib/analyticsSessionStatus';

const statisticsColumnHelper = createColumnHelper<WebinarStatsRow>();

const PRESENCE_RATE_TONE_CLASS = {
  neutral: 'text-role-tertiary',
  success: 'text-role-success',
  error: 'text-role-error',
} as const;

type WebinarStatsTableProps = {
  data: WebinarStatsRow[];
  fallbackTimezone?: string;
};

export const WebinarStatsTable = ({ data, fallbackTimezone }: WebinarStatsTableProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();

  const handleRowClick = (sessionId: number) => {
    navigate(`/analytics/sessions/${sessionId}`);
  };

  const columns = [
    statisticsColumnHelper.accessor('title', {
      header: t('table.columns.title'),
      cell: (info) => (
        <div className="flex flex-col gap-1 py-1 max-w-[250px]">
          <span className="font-medium text-role-default truncate">{info.getValue()}</span>
          <WebinarStatusTag
            sessionType={info.row.original.type}
            status={info.row.original.status}
          />
        </div>
      ),
    }),
    statisticsColumnHelper.accessor('projectionDate', {
      header: t('table.columns.projectionDate'),
      cell: (info) => (
        <span className="text-role-tertiary text-sm whitespace-nowrap">
          {getFormattedDate(info.getValue(), info.row.original.timezone ?? fallbackTimezone)}
        </span>
      ),
    }),
    statisticsColumnHelper.display({
      id: 'registrations',
      header: t('table.columns.attendees'),
      cell: (info) => (
        <div className="text-sm font-medium whitespace-nowrap">
          <span className="text-role-default font-bold">{info.row.original.participantsCount}</span>
          <span className="text-role-tertiary mx-1">/</span>
          <span className="text-role-tertiary">{info.row.original.registrantsCount}</span>
        </div>
      ),
    }),
    statisticsColumnHelper.accessor('presenceRatePercentage', {
      header: t('table.columns.presenceRate'),
      cell: (info) => {
        const tone = resolvePresenceRateTone(info.getValue(), info.row.original.status);
        return (
          <span className={`text-sm font-bold ${PRESENCE_RATE_TONE_CLASS[tone]}`}>
            {info.getValue()}%
          </span>
        );
      },
    }),
    statisticsColumnHelper.accessor('averageWatchTime', {
      header: t('table.columns.averageWatchTime'),
      cell: (info) => (
        <span className="text-role-secondary text-sm whitespace-nowrap">
          {resolveNotStartedKpiValue(info.getValue(), info.row.original.status)}
        </span>
      ),
    }),
    statisticsColumnHelper.accessor('offerClicksCount', {
      header: t('table.columns.offerClicks'),
      cell: (info) => <span className="font-bold text-role-default">{info.getValue()}</span>,
    }),
  ];

  const tableInstance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="w-full bg-white border border-medium rounded-[20px] overflow-hidden shadow-sm">
      <div className="overflow-x-auto no-scrollbar">
        <table className="w-full text-left border-collapse min-w-[900px]">
          <thead>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-neutral-50/50 border-b border-medium">
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-6 py-4 text-sm text-role-tertiary font-normal">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-medium">
            {tableInstance.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row.original.sessionId)}
                className="hover:bg-theme-light transition-colors group cursor-pointer"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-6 py-4 font-poppins">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
